import { useMemo } from 'react';
import type { Logic } from '@/logic-engine';
import type { EditorScreen } from '@/store/editorSlice';
// WEB-2015: прямой модуль вместо бочки `@/logic-engine` — бочка ре-экспортирует
// схемы правил (zod) редактора, и они уезжали покупателю в первом экране.
import { buildFunnelGraph, visitorPathFrom } from '@/logic-engine/funnel-graph';
import { resolveEditorBlockOptions } from '@/components/editor/resolveEditorBlockOptions';

/**
 * ── WEB-1812: ЧЕСТНЫЙ ПРОГРЕСС ПО ФАКТИЧЕСКОМУ ПУТИ ──────────────────────
 *
 * Решение владельца 02.09: «нормально сделать чтоб никого не обманывало и
 * все считалось». Раньше и счётчик в шапке, и числа в событиях аналитики
 * считались по ОБЩЕМУ списку экранов: посетителя, которого правило унесло с
 * экрана 3 на экран 30 из 40, продукт объявлял прошедшим 75 % — полоса
 * прыгала, а в статистике мгновенно ставились отметки «прошёл половину» и
 * «прошёл почти всё» (SQL quiz50/quiz90 делит присланные числа).
 *
 * ЧИСЛИТЕЛЬ — сколько экранов человек РЕАЛЬНО прошёл: длина стека истории
 * плюс текущий экран (в превью редактора стека нет, там счёт по показанному
 * экрану — прежнее поведение WEB-916).
 *
 * ЗНАМЕНАТЕЛЬ — длина ЕГО пути: пройденное плюс остаток от текущего экрана
 * до конца по безусловной ступени лестницы плеера. Остаток считает общий
 * вычислитель графа (`visitorPathFrom`), а не своя ходилка по экранам:
 * третья копия лестницы переходов — тот самый класс дефекта, ради которого
 * WEB-1807 и делали. Прыжок по правилу пересчитывает знаменатель сам собой:
 * путь считается от НОВОГО текущего экрана.
 *
 * ЛИНЕЙНАЯ ВОРОНКА НЕ ДВИГАЕТСЯ: там пройденное + остаток = длина списка, и
 * числа остаются прежними байт-в-байт (закреплено тестом).
 *
 * `passedCount` — длина стека истории плеера; `safeIndex` — показанный экран.
 * Отдаёт [граф, индекс шага, номер шага с единицы, длина пути]; граф нужен
 * наружу — им же шапка по разделам считает свой прогресс. Кортеж и
 * позиционные параметры, а не объекты: имена ключей минификатор не сжимает,
 * а запас веса первого экрана покупателя — десятки байт.
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx без изменений поведения; вызов
 * стоит на прежнем месте порядка хуков плеера.
 */
export function useQuizVisitorPath(
  screens: EditorScreen[],
  logic: Logic,
  isControlled: boolean,
  safeIndex: number,
  passedCount: number,
) {
  const currentScreen = screens[safeIndex];
  const funnelGraph = useMemo(
    () =>
      buildFunnelGraph({
        screens,
        rules: logic,
        resolveOptions: resolveEditorBlockOptions,
      }),
    [screens, logic],
  );
  const stepIndex = isControlled ? safeIndex : passedCount;
  const stepNumber = stepIndex + 1;
  const visitorPathTotal = useMemo(() => {
    // ПРЕВЬЮ РЕДАКТОРА (controlled) — знаменатель это ДЛИНА СПИСКА (ревизия
    // пачки 9, Ф1. В превью нет посетителя и нет пути: экран выбирает автор
    // мышью, а числителем служит номер выбранного экрана В СПИСКЕ (WEB-916
    // п.1). Складывать этот номер с остатком ветки — смешивать две разные
    // величины: у воронки, где пятый экран кнопкой «Далее» уводит на
    // сороковой, автор видел «Шаг 5 из 6» при сорока экранах. Честное
    // «экран N из списка» и есть то, что автор в превью спрашивает.
    if (isControlled) return Math.max(screens.length, stepNumber);
    const ahead = currentScreen
      ? visitorPathFrom(funnelGraph, currentScreen.id).length
      : 0;
    // Путь пуст (экран без id, мусорное содержимое) — знаменателем остаётся
    // длина списка: прежнее поведение лучше нуля или единицы, которые
    // объявили бы каждого посетителя прошедшим воронку целиком.
    const total = ahead > 0 ? stepIndex + ahead : screens.length;
    return Math.max(total, stepNumber);
  }, [
    isControlled,
    funnelGraph,
    currentScreen,
    stepIndex,
    stepNumber,
    screens.length,
  ]);
  return [funnelGraph, stepIndex, stepNumber, visitorPathTotal] as const;
}
